import { Button, FormGroup, Input } from "reactstrap";
import Card from "@components/ui-components/card";
import Flex from "@components/ui-components/flex";
import ImageWrapper from "@components/ui-components/image-wrapper";
import { LoaderOverlay } from "@components/spinner";
import { RATING_VALUE } from "../misc/constants";
import themeConfig from "@src/configs/theme-config";
import useSubmit from "./use-submit";
import { useTranslation } from "react-i18next";

const SubmitSurvey = () => {
    const {
        loading,
        isSubmitted,
        setSelectedRating,
        handleSubmit,
        setAdditionalComment,
        survey,
        selectedRating,
        additionalComment
    } = useSubmit();
    const { t } = useTranslation();

    // Determine if we should show additional comment field based on selected rating

    // Get appropriate comment placeholder based on rating
    const getAdditionalCommentPlaceholder = () => {
        if (!survey) return "Additional comments";

        if (selectedRating !== null) {
            if (survey.survey_type === "csat") {
                if (selectedRating >= 3) {
                    return (
                        survey.additional_comments.satisfied || "Tell us what you liked"
                    );
                } else if (selectedRating === 2) {
                    return (
                        survey.additional_comments.neutral || "Please share your thoughts"
                    );
                } else {
                    return (
                        survey.additional_comments.unsatisfied ||
            "Tell us how we can improve"
                    );
                }
            }
        } else {
            if (selectedRating >= 9) {
                return survey.additional_comments.promoter || "Tell us what you liked";
            } else if (selectedRating >= 7) {
                return (
                    survey.additional_comments.passive || "Please share your thoughts"
                );
            } else {
                return (
                    survey.additional_comments.detractor || "Tell us how we can improve"
                );
            }
        }

        return "Additional comments";
    };

    // Get thank you message based on rating
    const getThankYouMessage = () => {
        if (selectedRating !== null) {
            if (survey.survey_type === "csat") {
                if (selectedRating >= 3) {
                    return (
                        survey.thank_you_messages?.satisfied ||
            "Thank you for your positive feedback!"
                    );
                } else if (selectedRating === 2) {
                    return (
                        survey.thank_you_messages?.neutral || "Thank you for your feedback!"
                    );
                } else {
                    return (
                        survey.thank_you_messages?.unsatisfied ||
            "Thank you for helping us improve!"
                    );
                }
            } else {
                if (selectedRating >= 9) {
                    return (
                        survey.thank_you_messages?.promoter ||
            "Thank you for your positive feedback!"
                    );
                } else if (selectedRating >= 7) {
                    return (
                        survey.thank_you_messages?.passive || "Thank you for your feedback!"
                    );
                } else {
                    return (
                        survey.thank_you_messages?.detractor ||
            "Thank you for helping us improve!"
                    );
                }
            }
        }
        return (
            survey.thank_you_messages?.satisfied || "Thank you for your feedback!"
        );
    };

    const renderRatingScale = () => {
        if (survey.survey_type === "csat") {
            return (
                <div className="mt-4">
                    <Flex className="mb-3 font-medium-2" justify="between" align="center">
                        {RATING_VALUE.map((label, index) => (
                            <Button
                                key={index}
                                onClick={() => setSelectedRating(index)}
                                color="primary"
                                outline={selectedRating === index}
                                active={selectedRating === index}
                                disabled={isSubmitted}>
                                {t(label)}
                            </Button>
                        ))}
                    </Flex>
                    <Flex justify="between" className="text-muted small">
                        <span>{t(survey.bad_rating_text || "Bad")}</span>
                        <span>{t(survey.good_rating_text || "Good")}</span>
                    </Flex>
                </div>
            );
        } else {
            // NPS
            return (
                <div className="mb-4">
                    <Flex
                        justify="between"
                        align="center"
                        className="mb-3 text-muted small font-medium-2">
                        <span>{t(survey.bad_rating_text || "Not likely at all")}</span>
                        <span>{t(survey.good_rating_text || "Extremely likely")}</span>
                    </Flex>
                    <Flex justify="start" wrap className="mb-3 font-medium-2">
                        {Array.from({ length: 11 }, (_, i) => (
                            <Button
                                key={i}
                                color="primary"
                                size="sm"
                                outline={selectedRating !== i}
                                active={selectedRating === i}
                                onClick={() => setSelectedRating(i)}
                                disabled={isSubmitted}
                                className="p-2 mb-25">
                                {i}
                            </Button>
                        ))}
                    </Flex>
                    <Flex justify="between" className="text-muted small font-medium-2">
                        <span>{t("Detractors (0-6)")}</span>
                        <span>{t("Passives (7-8)")}</span>
                        <span>{t("Promoters (9-10)")}</span>
                    </Flex>
                </div>
            );
        }
    };

    return (
        <LoaderOverlay
            active={loading}
            children={
                survey ? (
                    <Flex
                        justify="center"
                        align="center"
                        className="container-fluid min-vh-100 w-50">
                        <Card header={<h1 className="text-center mt-1">{t("Survey")}</h1>}>
                            <>
                                <div className="border rounded p-4 mb-3 survey-preview-container">
                                    {!isSubmitted ? (
                                        <>
                                            <Flex justify="center" align="center" className="mb-2">
                                                <ImageWrapper width={200} src={themeConfig.app.logo} />
                                            </Flex>
                                            {survey.additional_message && (
                                                <h3 className="mb-4 text-center">
                                                    {t(survey.additional_message)}
                                                </h3>
                                            )}
                                            <h4 className="my-3 text-center">
                                                {t(
                                                    survey.question_message ||
                            "How likely are you to recommend our service to others?"
                                                )}
                                            </h4>
                                            {renderRatingScale()}

                                            <div className="mb-4">
                                                <FormGroup>
                                                    <Input
                                                        type="textarea"
                                                        value={additionalComment}
                                                        onChange={(e) => setAdditionalComment(e.target.value)
                                                        }
                                                        placeholder={getAdditionalCommentPlaceholder()}
                                                        rows={3}
                                                        className="survey-preview-textarea"
                                                        disabled={isSubmitted}/>
                                                </FormGroup>
                                            </div>

                                            <Flex justify="end">
                                                <Button
                                                    color="primary"
                                                    onClick={handleSubmit}
                                                    disabled={isSubmitted}>
                                                    {t("Submit Feedback")}
                                                </Button>
                                            </Flex>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-center py-3">
                                                <h1>{t("Thank You!")}</h1>
                                                <ImageWrapper width={100} src="common/success.svg" />
                                            </div>
                                            <div className="border-top pt-3 font-medium-5 text-center">
                                                {t(getThankYouMessage())}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </>
                        </Card>
                    </Flex>
                ) : (
                    <Flex
                        justify="center"
                        align="center"
                        className="container-fluid w-50 mt-2">
                        <Card
                            containerClass="p-5"
                            header={
                                <h2 className="text-center text-danger">
                                    {t("Survey Not Found")}
                                </h2>
                            }
                            bodyClass="text-center">
                            <>
                                <h3 className="text-danger">
                                    {t(
                                        "Unable to load the requested survey. Please check the URL and try again."
                                    )}
                                </h3>
                            </>
                        </Card>
                    </Flex>
                )
            }/>
    );
};

export default SubmitSurvey;
